import { useState, useEffect } from 'react';
import { Button } from './Button';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';
import { getStatuses, getEvents, getMessageTypes, getActivities, getGroups } from '../services/api';

export const BulkEditForm = ({ onSubmit, onCancel, selectedCount }) => {
  const [formData, setFormData] = useState({
    status_id: '',
    event_id: '',
    action_date: '',
    campaign_name: '',
    group_id: '',
    preferred_days: [],
    message_types: [],
  });
  const [statuses, setStatuses] = useState([]);
  const [events, setEvents] = useState([]);
  const [messageTypes, setMessageTypes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [groups, setGroups] = useState([]);
  const [appendMessageTypes, setAppendMessageTypes] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    fetchStatuses();
    fetchEvents();
    fetchMessageTypes();
    fetchActivities();
    fetchGroups();
  }, []);

  const fetchStatuses = async () => {
    try {
      const data = await getStatuses();
      setStatuses(data);
    } catch (error) {
      toast.error(t('failedToLoadStatuses'));
    }
  };

  const fetchEvents = async () => {
    try {
      const data = await getEvents({ is_active: 'true', upcoming_only: 'true' });
      setEvents(data);
    } catch (error) {
      toast.error(t('failedToLoadEvents') || 'Failed to load events');
    }
  };

  const fetchMessageTypes = async () => {
    try {
      const data = await getMessageTypes();
      setMessageTypes(data);
    } catch (error) {
      console.error('Failed to load message types:', error);
      toast.error(t('failedToLoadMessageTypes') || 'Failed to load message types');
    }
  };

  const fetchActivities = async () => {
    try {
      const data = await getActivities(true); // Only active activities
      setActivities(data);
    } catch (error) {
      console.error('Failed to load activities:', error);
      toast.error(t('failedToLoadActivities') || 'Failed to load activities');
    }
  };

  const fetchGroups = async () => {
    try {
      const data = await getGroups(true); // Only active groups
      setGroups(data);
    } catch (error) {
      console.error('Failed to load groups:', error);
      toast.error(t('failedToLoadGroups') || 'Failed to load groups');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'event_id' && value) {
      const selectedEvent = events.find(event => event.id === value);
      if (selectedEvent) {
        const eventDate = new Date(selectedEvent.event_date);
        const year = eventDate.getFullYear();
        const month = String(eventDate.getMonth() + 1).padStart(2, '0');
        const day = String(eventDate.getDate()).padStart(2, '0');
        const hours = String(eventDate.getHours()).padStart(2, '0');
        const minutes = String(eventDate.getMinutes()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;

        setFormData(prev => ({
          ...prev,
          [name]: value,
          action_date: formattedDate
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePreferredDayToggle = (day) => {
    setFormData(prev => {
      const currentDays = prev.preferred_days || [];
      const newDays = currentDays.includes(day)
        ? currentDays.filter(d => d !== day)
        : [...currentDays, day];
      return { ...prev, preferred_days: newDays };
    });
  };

  const handleMessageTypeToggle = (typeId) => {
    setFormData(prev => {
      const currentTypes = prev.message_types || [];
      const newTypes = currentTypes.includes(typeId)
        ? currentTypes.filter(id => id !== typeId)
        : [...currentTypes, typeId];
      return { ...prev, message_types: newTypes };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Filter out empty values
    const updates = {};
    if (formData.status_id) updates.status_id = formData.status_id;
    if (formData.event_id) updates.event_id = formData.event_id;
    if (formData.action_date) updates.action_date = new Date(formData.action_date).toISOString();
    if (formData.group_id) updates.group_id = parseInt(formData.group_id, 10);

    const field_data = {};
    if (formData.campaign_name) field_data.campaign_name = formData.campaign_name;
    if (formData.preferred_days.length > 0) field_data.preferred_days = formData.preferred_days;
    if (formData.message_types.length > 0) field_data.message_types = formData.message_types;

    if (Object.keys(field_data).length > 0) updates.field_data = field_data;

    if (Object.keys(updates).length === 0) {
      toast.error(t('noChangesSelected') || 'Please select at least one field to update');
      return;
    }

    setLoading(true);
    try {
      await onSubmit(updates, { appendMessageTypes });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-blue-800">
          {t('bulkEditInfo') || 'Changes will be applied to'} <strong>{selectedCount}</strong> {t('selectedLeads')}
        </p>
        <p className="text-xs text-blue-600 mt-1">
          {t('bulkEditNote') || 'Only selected fields will be updated. Empty fields will remain unchanged.'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('status')}
        </label>
        <select
          name="status_id"
          value={formData.status_id}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('noChange') || '-- No Change --'}</option>
          {statuses.map((status) => (
            <option key={status.id} value={status.id}>
              {status.status_name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('activity') || 'Activity/Campaign'}
        </label>
        <select
          name="campaign_name"
          value={formData.campaign_name}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('noChange') || '-- No Change --'}</option>
          {activities.map((activity) => (
            <option key={activity.id} value={activity.activity_name}>
              {activity.activity_name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('group') || 'Group'}
        </label>
        <select
          name="group_id"
          value={formData.group_id}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('noChange') || '-- No Change --'}</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.group_name} ({group.member_count || 0} {t('members') || 'members'})
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('assignToEvent') || 'Assign to Event'}
        </label>
        <select
          name="event_id"
          value={formData.event_id}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('noChange') || '-- No Change --'}</option>
          {events.map((event) => {
            const assignedCount = parseInt(event.assigned_count) || 0;
            const capacity = event.capacity || 0;
            const isFull = capacity > 0 && assignedCount >= capacity;
            const eventDate = new Date(event.event_date).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });

            return (
              <option key={event.id} value={event.id} disabled={isFull}>
                {event.event_name} - {eventDate}
                {isFull ? ` (${t('full') || 'Full'})` : capacity > 0 ? ` (${assignedCount}/${capacity})` : ''}
              </option>
            );
          })}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('actionDate') || 'Action Date'}
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="date"
            name="action_date"
            value={formData.action_date ? formData.action_date.split('T')[0] : ''}
            onChange={(e) => {
              const dateValue = e.target.value;
              const timeValue = formData.action_date ? formData.action_date.split('T')[1] : '00:00';
              handleChange({ target: { name: 'action_date', value: dateValue ? `${dateValue}T${timeValue}` : '' } });
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!!formData.event_id}
          />
          <input
            type="time"
            name="action_time"
            value={formData.action_date ? formData.action_date.split('T')[1] || '' : ''}
            onChange={(e) => {
              const timeValue = e.target.value;
              const dateValue = formData.action_date ? formData.action_date.split('T')[0] : '';
              if (dateValue) {
                handleChange({ target: { name: 'action_date', value: `${dateValue}T${timeValue}` } });
              }
            }}
            step="1800"
            pattern="[0-9]{2}:[0-9]{2}"
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!!formData.event_id}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('preferredDays') || 'Preferred Days'}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => (
            <label
              key={day}
              className="flex items-center gap-2 cursor-pointer p-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              <input
                type="checkbox"
                checked={formData.preferred_days?.includes(day) || false}
                onChange={() => handlePreferredDayToggle(day)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-xs text-gray-700">{t(day)}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('messageTypes') || 'Message Types'}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {messageTypes.map((type) => (
            <label
              key={type.id}
              className="flex items-center gap-2 cursor-pointer p-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              <input
                type="checkbox"
                checked={formData.message_types?.includes(type.id) || false}
                onChange={() => handleMessageTypeToggle(type.id)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-xs text-gray-700">{type.name}</span>
            </label>
          ))}
        </div>
        {formData.message_types.length > 0 && (
          <div className="mt-3 flex items-start">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={appendMessageTypes}
                onChange={(e) => setAppendMessageTypes(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                {t('appendMessageTypes') || 'Add to existing message types (don\'t replace)'}
              </span>
            </label>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          className="flex-1"
        >
          {t('updateSelected') || 'Update Selected'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          className="flex-1"
        >
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
};
